/**
 * Presentational rules for the confirmation card's key/value summary.
 *
 * The confirmation card is the only gate between the user and a real write, so
 * it must show every field the write will carry. The gateway's pause payload
 * carries the pending write's args near-raw; everything renders EXCEPT
 * machine/reference keys (`*_id`, bare ids — those already name the target in
 * the summary line) and secret-shaped keys. An allowlist was tried first and
 * rotted instantly: every new tool's fields silently vanished from the card,
 * so users approved price changes without seeing the price.
 *
 * Fix round 2, item #4: captions and booleans follow the draft's `language`
 * (an ISO 639-1 code, forwarded by the backend as the turn's reply language).
 * Values themselves — names, emails, free text — are never translated; only
 * `card-labels.ts`'s lookups (captions, true/false) are. `language` defaults
 * to English, so every call site that predates this change is unaffected.
 *
 * Fix round 3, task 2: the backend may additionally resolve an id-valued arg
 * to a human name via `argument_labels`, an OPTIONAL `{path: "Name (#id)"}`
 * map keyed by the argument's PATH — a top-level arg's own key ("issued_to"),
 * or "values.<key>" for a key inside change_domain_record's `values` (so a
 * bare "student" label can never leak into "values.student", or vice versa).
 * A path's label, when present and a string, replaces that row's whole
 * rendered value (array args included) and is shown verbatim — never
 * translated, only truncated like any other value. A hidden key (`*_id`,
 * tokens, secrets) stays hidden even when a label exists for it. Missing
 * `argument_labels`, or the path absent from it, is today's behaviour.
 *
 * Fix round 3, task 3: `argument_labels` may also carry list-index paths
 * "<key>.<index>.<field>" (e.g. "line_items.0.product"). When an array of
 * objects renders as one-liners, an item's field looks up that path and
 * uses the label (verbatim, truncated) instead of the field's raw value —
 * so a line item's "Product: 24" becomes "Product: QA-MULTI Service (#24)".
 * The whole-array label above still wins: it is checked first and, when
 * present, the item-level lookup never runs. The same paths apply inside
 * "values.<key>.<index>.<field>".
 */
import { dayjsUtils } from '@shared/utils/helpers/dayjs';

import { type Language, fieldCaption, booleanLabel } from './card-labels';

const HIDDEN_EXACT = new Set(['id', 'organization', 'confirmation_token']);

const HIDDEN_PATTERN = /(_id$|token|secret|password|api_key)/;

const VALUE_LIMIT = 120;

/** ISO date (`2026-10-26`) or datetime (`2026-10-26T10:00:00+00:00`). */
const ISO_DATE_RE =
  /^\d{4}-\d{2}-\d{2}(?:[T\s]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function truncate(text: string): string {
  return text.length > VALUE_LIMIT
    ? `${text.slice(0, VALUE_LIMIT - 1)}…`
    : text;
}

/** Readable date for approval cards — date-only → `26 Oct 2026`,
 * datetime → `26 Oct 2026, 10:00`. Uses the wall-clock in the ISO string
 * (not the viewer's local offset) so staff approve the time the write carries.
 * Non-ISO strings pass through. */
export function formatConfirmationDate(value: string): string {
  const trimmed = value.trim();
  if (!ISO_DATE_RE.test(trimmed)) return trimmed;
  const withTime = trimmed.match(/^(\d{4}-\d{2}-\d{2})[T\s](\d{2}:\d{2})/);
  if (withTime) {
    const parsed = dayjsUtils(
      `${withTime[1]} ${withTime[2]}`,
      'YYYY-MM-DD HH:mm'
    );
    return parsed.isValid() ? parsed.format('DD MMM YYYY, HH:mm') : trimmed;
  }
  const parsed = dayjsUtils(trimmed.slice(0, 10), 'YYYY-MM-DD');
  return parsed.isValid() ? parsed.format('DD MMM YYYY') : trimmed;
}

/** Primitives as-is (booleans translated via `booleanLabel`); arrays joined,
 * object items rendered via `readableObject` instead of JSON.stringify — a
 * card never shows raw JSON.
 *
 * `path` and `argumentLabels` are only used for an array of objects: each
 * item's one-liner can then look up its own fields at "<path>.<index>.
 * <field>" (fix round 3, task 3). A primitive array, or a call with no
 * `path`, renders exactly as before. */
function displayPrimitiveOrArray(
  value: unknown,
  language: Language,
  path?: string,
  argumentLabels?: unknown
): string | null {
  if (typeof value === 'boolean') {
    return booleanLabel(language, value);
  }
  if (typeof value === 'string') {
    const text = value.trim();
    if (text === '') return null;
    return formatConfirmationDate(text);
  }
  if (typeof value === 'number') {
    const text = String(value).trim();
    return text === '' ? null : text;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    const parts = value.map((item, index) => {
      if (isPlainObject(item)) {
        const itemPath = path === undefined ? undefined : `${path}.${index}`;
        return readableObject(item, language, argumentLabels, itemPath);
      }
      if (typeof item === 'boolean') return booleanLabel(language, item);
      return String(item);
    });
    return truncate(parts.join(', '));
  }
  return null;
}

/**
 * A plain object nested inside a row's value (e.g. a key inside
 * change_domain_record's `values`, or an array item) never renders as
 * `[object Object]` or raw JSON — it reads as "Caption: value, Caption:
 * value", captioned and hidden the same way a top-level row is.
 *
 * `itemPath` (fix round 3, task 3) is this object's own path when it is one
 * item of an array — e.g. "line_items.0". Each field first checks for a
 * label at "<itemPath>.<field>" before falling back to its raw value, same
 * as a top-level row does for its own path.
 */
function readableObject(
  obj: Record<string, unknown>,
  language: Language,
  argumentLabels?: unknown,
  itemPath?: string
): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(obj)) {
    if (HIDDEN_EXACT.has(k) || HIDDEN_PATTERN.test(k)) continue;
    const label =
      itemPath === undefined
        ? null
        : labelFor(`${itemPath}.${k}`, argumentLabels);
    const text =
      label ??
      (isPlainObject(v)
        ? readableObject(v, language, argumentLabels)
        : displayPrimitiveOrArray(v, language));
    if (text === null || text === '') continue;
    parts.push(`${fieldCaption(language, k)}: ${text}`);
  }
  return truncate(parts.join(', '));
}

/**
 * The label for a row's PATH, if `argumentLabels` carries a string one — the
 * value that wins over whatever `displayTop`/`displayRow` would have
 * rendered. A missing map, a missing path, or a non-string label all fall
 * through to `null`, so the caller renders the raw value exactly as before.
 *
 * Polish round: an empty or whitespace-only label is treated the same as a
 * missing one — a resolver that found nothing should never blank out a row
 * or drop a field; the raw value renders instead.
 */
function labelFor(path: string, argumentLabels: unknown): string | null {
  if (!isPlainObject(argumentLabels)) return null;
  const label = argumentLabels[path];
  if (typeof label !== 'string' || label.trim() === '') return null;
  return truncate(label);
}

/** A top-level arg's display text. A plain-object value is dropped here (not
 * shown as a row) — the only plain object a top-level row expands is
 * `change_domain_record`'s `values`, handled separately below.
 *
 * `path` and `argumentLabels` (fix round 3, task 3) let an array-of-objects
 * value label its items by "<path>.<index>.<field>". */
function displayTop(
  value: unknown,
  language: Language,
  path?: string,
  argumentLabels?: unknown
): string | null {
  return displayPrimitiveOrArray(value, language, path, argumentLabels);
}

/** Display text for a value one level inside an expanded `values` object —
 * a plain object here is "nested deeper" and renders as a one-liner. */
function displayRow(
  value: unknown,
  language: Language,
  path?: string,
  argumentLabels?: unknown
): string | null {
  if (isPlainObject(value)) {
    const text = readableObject(value, language, argumentLabels);
    return text === '' ? null : text;
  }
  return displayPrimitiveOrArray(value, language, path, argumentLabels);
}

/**
 * The card's [label, value] rows: every argument the write will carry, in args
 * order, minus reference/secret keys; labels captioned in `language` (English
 * by default), long values truncated.
 *
 * `change_domain_record`'s `arguments` is `{resource, action, values: {...}}`
 * — the raw write. When `values` is a plain object, its keys expand into one
 * row each (so staff see the fields they're actually saving) and the
 * top-level `resource`/`action` rows are suppressed, since the card's summary
 * already names the target ("Update saved view (record #1 \"X\") ..."). Any
 * other tool's top-level `resource`/`action` keys render as ordinary rows.
 */
export function confirmationDisplayEntries(
  args: unknown,
  language: Language = 'en',
  argumentLabels?: unknown
): Array<[string, string]> {
  if (!isPlainObject(args)) return [];
  const expandValues = isPlainObject(args.values);
  const out: Array<[string, string]> = [];
  for (const [k, v] of Object.entries(args)) {
    if (HIDDEN_EXACT.has(k) || HIDDEN_PATTERN.test(k)) continue;
    if (expandValues && (k === 'resource' || k === 'action')) continue;
    if (k === 'values' && expandValues) {
      for (const [vk, vv] of Object.entries(v as Record<string, unknown>)) {
        if (HIDDEN_EXACT.has(vk) || HIDDEN_PATTERN.test(vk)) continue;
        const text =
          labelFor(`values.${vk}`, argumentLabels) ??
          displayRow(vv, language, `values.${vk}`, argumentLabels);
        if (text === null) continue;
        out.push([fieldCaption(language, vk), text]);
      }
      continue;
    }
    const text =
      labelFor(k, argumentLabels) ?? displayTop(v, language, k, argumentLabels);
    if (text === null) continue;
    out.push([fieldCaption(language, k), text]);
  }
  return out;
}
